import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

// 1. Initialize the Master Context Engine
const LedgerContext = createContext();

// 2. Export a custom hook for rapid data access across the app
export const useLedger = () => {
  const context = useContext(LedgerContext);
  if (!context) {
    throw new Error("useLedger must be used within a LedgerProvider");
  }
  return context;
};

// 3. The Centralized Provider Node
export const LedgerProvider = ({ children }) => {
  
  // === AUTH & SYSTEM STATE ===
  const [user, setUser] = useState(null);
  const [isDemoMode, setIsDemoMode] = useState(false);

  // === PRIMARY LEDGER DATA ARRAYS ===
  const [bills, setBills] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [todos, setTodos] = useState([]);
  const [paydayConfig, setPaydayConfig] = useState({
    frequency: "Weekly",
    "Payday 1": { date: "", income: "" },
    "Payday 2": { date: "", income: "" },
    "Payday 3": { date: "", income: "" },
    "Payday 4": { date: "", income: "" },
    "Payday 5": { date: "", income: "" }
  });

  // === GLOBAL PREFERENCES (With Permanent Memory & Crash-Safe Fallbacks) ===
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  const [signatureColor, setSignatureColor] = useState(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem("lp_signature_color");
        if (saved) return saved;
      } catch (e) {
        console.warn("Storage read error for signature color:", e);
      }
    }
    return "#1877F2";
  });

  const [currentCurrency, setCurrentCurrency] = useState(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem("lp_currency");
        if (saved) return saved;
      } catch (e) {
        console.warn("Storage read error for currency:", e);
      }
    }
    return "USD ($)";
  });

  // SYNC THEME & CURRENCY PREFERENCES TO LOCAL STORAGE
  useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem("lp_signature_color", signatureColor);
      } catch (e) {
        console.warn("Storage write error for signature color:", e);
      }
    }
  }, [signatureColor]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem("lp_currency", currentCurrency);
      } catch (e) {
        console.warn("Storage write error for currency:", e);
      }
    }
  }, [currentCurrency]);

  // DERIVE DYNAMIC SYMBOL FOR GLOBAL APP-WIDE CONSUMPTION
  const currencySymbol = useMemo(() => {
    if (currentCurrency.includes("€")) return "€";
    if (currentCurrency.includes("£")) return "£";
    if (currentCurrency.includes("¥")) return "¥";
    return "$";
  }, [currentCurrency]);

  // === INJECTED ENTREPRENEUR MODE STATE (With Permanent Memory) ===
  const [isEntrepreneurMode, setIsEntrepreneurMode] = useState(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem("lp_entrepreneur_mode");
        if (saved) return JSON.parse(saved);
      } catch (e) {
        console.warn("Storage read error for entrepreneur mode:", e);
      }
    }
    return false;
  });

  useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem("lp_entrepreneur_mode", JSON.stringify(isEntrepreneurMode));
      } catch (e) {
        console.warn("Storage write error for entrepreneur mode:", e);
      }
    }
  }, [isEntrepreneurMode]);

  // === TAXONOMY & CATEGORY MATRICES ===
  const [modernCategories, setModernCategories] = useState([
    { group: "Income & Wealth", items: ["Primary Salary", "Side Hustle / Gig", "Tips / Cash", "Investments / Crypto", "Transfers (Venmo/Zelle)", "Refunds & Adjustments", "Cash App", "PayDay Loans", "Unemployment", "Retirement / 401k", "Benefits", "My Goals"] },
    { group: "Housing & Utilities", items: ["Rent / Mortgage", "Electric / Gas", "Water / Trash", "Internet / Wi-Fi", "Home Goods / Maintenance", "Cell Phone"] },
    { group: "Transit & Travel", items: ["Gas / Fuel", "Rideshare (Uber/Lyft)", "Public Transit", "Auto Loan / Maintenance", "Parking / Tolls", "Airplane / Flights", "Hotel / Lodging", "Taxi / Car Rental"] },
    { group: "Food & Drink", items: ["Groceries", "Dining Out", "Delivery (DoorDash/Eats)", "Coffee / Tea", "Bars / Nightlife", "Convenient Store", "Fast Food", "Gas Station"] },
    { group: "Digital Life", items: ["Streaming (Netflix/Hulu)", "Music (Spotify/Apple)", "Software / Cloud", "Gaming", "Creators (Patreon/Twitch)", "Google Play Store", "Apple App Store"] },
    { group: "Shopping & Lifestyle", items: ["Amazon / E-commerce", "Clothing / Fashion", "Personal Care / Grooming", "Fitness / Gym", "Events / Concerts", "Pet Care", "Fun Recreation"] },
    { group: "Financial", items: ["Savings Transfer", "Credit Card Payment", "Debt Payoff", "Bank Fees / Interest"] },
    { group: "Health", items: ["Medical / Doctor", "Pharmacy / Rx", "Dental / Vision", "Therapy / Mental Health", "Health Insurance", "Fitness / Wellness"] },
    { group: "Entrepreneur", items: ["Domain / Hosting", "Software / SaaS", "AI Subscriptions", "Marketing & Ads", "Contractors & Freelancers", "Business Fees / LLC", "Office Supplies"] },
    { group: "Other", items: ["Miscellaneous Expense", "Charity / Gifts", "Other"] }
  ]);

  // Memory Notebook Loaders (Crash-Protected)
  const [recentBillCategories, setRecentBillCategories] = useState(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem("lp_recent_bill_cat");
        if (saved) return JSON.parse(saved);
      } catch (e) {
        console.warn("Storage read error for recent bill categories:", e);
      }
    }
    return [];
  });
  
  const [recentIncomeCategories, setRecentIncomeCategories] = useState(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem("lp_recent_inc_cat");
        if (saved) return JSON.parse(saved);
      } catch (e) {
        console.warn("Storage read error for recent income categories:", e);
      }
    }
    return [];
  });
  
  const [recentExpenseCategories, setRecentExpenseCategories] = useState(() => {
    if (typeof window !== "undefined") {
      try {
        const saved = localStorage.getItem("lp_recent_exp_cat");
        if (saved) return JSON.parse(saved);
      } catch (e) {
        console.warn("Storage read error for recent expense categories:", e);
      }
    }
    return [];
  });

  // PERMANENT STORAGE SYNC FOR RECENT CATEGORIES
  useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem("lp_recent_bill_cat", JSON.stringify(recentBillCategories));
      } catch (e) {
        console.warn("Storage write error for recent bill categories:", e);
      }
    }
  }, [recentBillCategories]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem("lp_recent_inc_cat", JSON.stringify(recentIncomeCategories));
      } catch (e) {
        console.warn("Storage write error for recent income categories:", e);
      }
    }
  }, [recentIncomeCategories]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem("lp_recent_exp_cat", JSON.stringify(recentExpenseCategories));
      } catch (e) {
        console.warn("Storage write error for recent expense categories:", e);
      }
    }
  }, [recentExpenseCategories]);

  // === THE GLOBAL NERVOUS SYSTEM PAYLOAD (OPTIMIZED WITH CACHE) ===
  const value = useMemo(() => ({
    user, setUser,
    isDemoMode, setIsDemoMode,
    bills, setBills,
    transactions, setTransactions,
    accounts, setAccounts,
    todos, setTodos,
    paydayConfig, setPaydayConfig,
    isDarkMode, setIsDarkMode,
    signatureColor, setSignatureColor,
    currentCurrency, setCurrentCurrency,
    currencySymbol,
    isEntrepreneurMode, setIsEntrepreneurMode,
    modernCategories, setModernCategories,
    recentBillCategories, setRecentBillCategories,
    recentIncomeCategories, setRecentIncomeCategories,
    recentExpenseCategories, setRecentExpenseCategories
  }), [
    user,
    isDemoMode,
    bills,
    transactions,
    accounts,
    todos,
    paydayConfig,
    isDarkMode,
    signatureColor,
    currentCurrency,
    currencySymbol,
    isEntrepreneurMode,
    modernCategories,
    recentBillCategories,
    recentIncomeCategories,
    recentExpenseCategories
  ]);

  return (
    <LedgerContext.Provider value={value}>
      {children}
    </LedgerContext.Provider>
  );
};
